import type { ReactNode } from 'react';
import { placeEvents, type AgendaEvent } from './logic.ts';

/**
 * Agenda da semana (ADR 0128): uma coluna por dia e as horas na esquerda. Os compromissos que se
 * encontram dividem a coluna. No celular a grade rola de lado, com a coluna das horas fixa.
 */
export function WeekAgenda<E extends AgendaEvent>({
  days,
  events,
  from = 8 * 60,
  to = 18 * 60,
  renderEvent,
  label,
  today,
}: {
  /** O cabeçalho de cada dia, já formatado ("seg 3"). */
  days: readonly ReactNode[];
  events: readonly E[];
  /** Primeira e última hora mostradas, em minutos desde a meia-noite. */
  from?: number;
  to?: number;
  renderEvent: (event: E) => ReactNode;
  /** Nome da agenda para leitores de tela. */
  label: string;
  /** O índice do dia de hoje, destacado. */
  today?: number;
}) {
  const placed = placeEvents(events, days.length, from, to);
  const hours: number[] = [];
  for (let m = Math.ceil(from / 60) * 60; m < to; m += 60) hours.push(m);
  const hourHeight = 3; // rem por hora
  const height = ((to - from) / 60) * hourHeight;
  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      className="ui-panel overflow-x-auto rounded-lg bg-sheet"
    >
      <div
        className="grid min-w-[44rem]"
        style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))` }}
      >
        <div className="sticky left-0 z-10 border-b border-rule bg-sheet" />
        {days.map((d, i) => (
          <div
            key={i}
            className={`border-b border-l border-rule px-2 py-2 text-center text-xs font-medium ${
              i === today ? 'text-brand-text' : 'text-muted'
            }`}
          >
            {d}
          </div>
        ))}
        <div className="sticky left-0 z-10 bg-sheet" style={{ height: `${height}rem` }}>
          {hours.map((m) => (
            <span
              key={m}
              className="absolute right-2 -translate-y-1/2 text-[11px] text-muted tabular-nums"
              style={{ top: `${((m - from) / 60) * hourHeight}rem` }}
            >
              {String(m / 60).padStart(2, '0')}:00
            </span>
          ))}
        </div>
        {days.map((_, day) => (
          <div
            key={day}
            className={`relative border-l border-rule ${day === today ? 'bg-brand-soft/30' : ''}`}
            style={{ height: `${height}rem` }}
          >
            {hours.map((m) => (
              <span
                key={m}
                aria-hidden
                className="absolute inset-x-0 border-t border-rule/60"
                style={{ top: `${((m - from) / 60) * hourHeight}rem` }}
              />
            ))}
            {placed
              .filter((p) => p.event.day === day)
              .map((p) => (
                <div
                  key={p.event.id}
                  className="absolute overflow-hidden rounded-md border-l-2 border-accent bg-brand-soft px-1.5 py-1 text-xs text-brand-text"
                  style={{
                    top: `${p.top * 100}%`,
                    height: `${p.height * 100}%`,
                    left: `calc(${p.left * 100}% + 2px)`,
                    width: `calc(${p.width * 100}% - 4px)`,
                  }}
                >
                  {renderEvent(p.event)}
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}
